import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Rating,
  Avatar,
  Tooltip,
  Fade,
  Zoom,
  useTheme,
  alpha,
  Badge
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  LocationOn as LocationIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  Euro as EuroIcon,
  AccessTime as AccessTimeIcon,
  Visibility as VisibilityIcon,
  Message as MessageIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ShoppingCart as ShoppingCartIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fr, ar, enUS } from 'date-fns/locale';
import CarouselSection from './sections/CarouselSection';
import InfoSection from './sections/InfoSection';
import PriceSection from './sections/PriceSection';
import AvatarActionsSection from './sections/AvatarActionsSection';
import PriceDisplay from '../common/PriceDisplay';
import { getCurrencySymbol } from '../../utils/currencyUtils';

const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${UPLOADS_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;
};

const SearchResultCard = ({ 
  listing, 
  layout = 'comfortable', 
  isSelected = false, 
  onSelect, 
  onHover,
  isProduct = false,   // true quand l'item est un produit marketplace
  cardSize = 'default' // 'default' | 'tall' — tall pour la page owner
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { showNotification } = useNotifications();
  const { isFavorite: isItemFavorite, isPending: isFavoritePending, toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  
  const [isHovered, setIsHovered] = useState(false);

  // Configuration des locales pour date-fns
  const locales = { fr, ar, en: enUS };
  const currentLocale = locales[i18n.language] || fr;

  const extractCityFromAddress = (address) => {
    if (!address || typeof address !== 'string') return '';
    const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 2) return parts[parts.length - 2];
    return parts[0] || '';
  };

  // Préparer les données (transformation pour compatibilité sections + normalisation ratings)
  const itemData = isProduct ? {
    ...listing,
    // Prix: transformer price_cents en price_daily pour compatibilité sections
    price_daily: (listing.price_cents || 0) / 100,
    price_hourly: undefined,
    price_weekly: undefined,
    price_monthly: undefined,
    // Location: privilégier la ville concise; garder l'adresse complète séparément pour "Afficher plus"
    city: listing.twinListing?.city || listing.pickup_location?.city || extractCityFromAddress(listing.pickup_location?.address) || '',
    region: listing.twinListing?.region || listing.country_code || '',
    fullAddress: listing.twinListing?.address || listing.pickup_location?.address || '',
    // Coordonnées pour la carte
    latitude: listing.twinListing?.latitude ?? listing.pickup_location?.lat ?? null,
    longitude: listing.twinListing?.longitude ?? listing.pickup_location?.lng ?? null,
    // Propriétaire normalisé pour les composants qui lisent listing.owner
    owner: listing.owner || listing.seller || null,
    // Mode de réservation: fallback sur le listing jumeau si disponible
    booking_mode: listing.twinListing?.booking_mode || listing.booking_mode,
    booking_settings: listing.twinListing?.booking_settings || listing.booking_settings,
    // Vraies reviews: fallback sur le listing jumeau si les champs produit ne sont pas présents
    // Convertir en nombre car certaines sources renvoient des strings
    rating_avg: parseFloat(listing.rating_avg ?? listing.rating ?? listing.twinListing?.rating_avg ?? listing.twinListing?.rating ?? 0) || 0,
    review_count: parseInt(listing.review_count ?? listing.rating_count ?? listing.twinListing?.review_count ?? listing.twinListing?.rating_count ?? 0, 10) || 0,
    // Variantes: ajouter un champ pour affichage
    productVariants: listing.variants || []
  } : {
    ...listing,
    rating_avg: parseFloat(listing.rating_avg ?? listing.rating ?? 0) || 0,
    review_count: parseInt(listing.review_count ?? listing.rating_count ?? 0, 10) || 0
  };

  const favoriteType = isProduct ? 'product' : 'listing';
  const isFavorite = isItemFavorite(favoriteType, itemData.id);
  const favoritePending = isFavoritePending(favoriteType, itemData.id);

  // Préparer les images pour le carrousel en évitant les doublons
  // 1. Normaliser l'image principale
  const mainImage = normalizeImageUrl(itemData.mainImageUrl) || null;

  // 2. Préparer les images en fonction des données disponibles
  let additionalImages = [];

  if (isProduct) {
    // Produit : images = JSONB array de chemins strings (ex: ['/uploads/products/...'])
    if (itemData.images && Array.isArray(itemData.images) && itemData.images.length > 0) {
      const firstImg = itemData.images[0];
      if (typeof firstImg === 'string') {
        additionalImages = itemData.images
          .map(url => normalizeImageUrl(url))
          .filter(url => url && url !== mainImage);
      } else if (firstImg && typeof firstImg === 'object' && firstImg.image_url) {
        additionalImages = itemData.images
          .map(img => normalizeImageUrl(img.image_url))
          .filter(url => url && url !== mainImage);
      }
    }
  } else {
    // Listing standard
    if (itemData.images && Array.isArray(itemData.images) && itemData.images.length > 0) {
      additionalImages = itemData.images
        .map(img => normalizeImageUrl(img.image_url))
        .filter(url => url && url !== mainImage);
    }
    // Ensuite, essayer avec listing.imageUrls
    else if (itemData.imageUrls && Array.isArray(itemData.imageUrls) && itemData.imageUrls.length > 0) {
      additionalImages = itemData.imageUrls
        .map(url => normalizeImageUrl(url))
        .filter(url => url && url !== mainImage);
    }
  }

  // 3. Combiner les sources d'images en évitant les doublons (déjà normalisées)
  const images = Array.from(new Set([
    mainImage,
    ...additionalImages
  ].filter(Boolean)));

  // Trier les images par order_index si disponible (listings seulement)
  if (!isProduct && itemData.images && Array.isArray(itemData.images) && itemData.images.length > 0) {
    try {
      // Créer un mapping d'URL normalisée vers ordre
      const orderMap = new Map();
      itemData.images.forEach(img => {
        if (img.image_url && img.order_index !== undefined) {
          orderMap.set(normalizeImageUrl(img.image_url), img.order_index);
        }
      });

      // Trier les images par order_index si disponible
      if (orderMap.size > 0) {
        images.sort((a, b) => {
          const orderA = orderMap.has(a) ? orderMap.get(a) : 999;
          const orderB = orderMap.has(b) ? orderMap.get(b) : 999;
          return orderA - orderB;
        });
      }
    } catch (error) {
      console.warn('Erreur lors du tri des images par ordre:', error);
    }
  }

  // Gestionnaires d'événements
  const handleFavoriteToggle = async (e) => {
    e.stopPropagation();

    if (!user) {
      showNotification(t('search.favorite.loginRequired'), 'info');
      return;
    }

    try {
      const result = await toggleFavorite({ type: favoriteType, targetId: itemData.id });
      if (!result?.ignored) {
        showNotification(
          result?.isFavorite
            ? t('search.favorite.added')
            : t('search.favorite.removed'),
          'success'
        );
      }
    } catch (error) {
      showNotification(
        error?.response?.data?.message || t('search.favorite.error', 'Impossible de mettre à jour vos favoris'),
        'error'
      );
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: itemData.title,
        text: itemData.description,
        url: window.location.href
      });
    } else {
      // Fallback pour les navigateurs qui ne supportent pas l'API Share
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleContact = (e) => {
    e.stopPropagation();
    // TODO: Ouvrir modal de contact
  };

  const handleCall = (e) => {
    e.stopPropagation();
    // TODO: Initier appel
  };

  const handleViewListing = (e) => {
    e.stopPropagation();
    // Open product order pages in a new tab; regular listings navigate in-app
    if (isProduct) {
      const path = `/product/${itemData.id}/order`;
      window.open(window.location.origin + path, '_blank');
    } else {
      // Open listing view in a new tab to match product behavior
      const path = `/view-listing/${itemData.id}`;
      window.open(window.location.origin + path, '_blank');
    }
    onSelect?.(itemData);
  };

  const handleOwnerClick = (e) => {
    e.stopPropagation();
    const owner = itemData.owner || itemData.seller;
    const ownerId = owner?.id;
    if (ownerId) {
      const path = `/owner/${ownerId}`;
      window.open(window.location.origin + path, '_blank');
    }
  };

  // Formatage des données
  const formatLocation = (listing) => {
    const parts = [listing.city, listing.region].filter(Boolean);
    return parts.join(', ') || t('listing.location.unknown');
  };

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const normalized = String(value).replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const inlinePrice = (() => {
    const symbol = getCurrencySymbol(itemData.currency || 'EUR');
    const isSlot = itemData.booking_mode === 'time_slot' || itemData.booking_mode === 'time_slot_capacity';

    if (isSlot && itemData.booking_settings) {
      const slotPrice = toNumber(itemData.booking_settings.price_per_slot || itemData.booking_settings.base_price);
      if (slotPrice > 0) {
        return { amount: `${Math.round(slotPrice)} ${symbol}`, period: '/slot' };
      }
    }

    const fallbackDaily = toNumber(itemData.price_cents) > 0 ? toNumber(itemData.price_cents) / 100 : 0;
    const best = [
      { v: toNumber(itemData.price_daily) || fallbackDaily, period: t('listing.price.period.daily.short', '/j') },
      { v: toNumber(itemData.price_hourly), period: t('listing.price.period.hourly.short', '/h') },
      { v: toNumber(itemData.price_weekly), period: t('listing.price.period.weekly.short', '/s') },
      { v: toNumber(itemData.price_monthly), period: t('listing.price.period.monthly.short', '/m') },
    ].find((p) => p.v > 0);

    if (!best) {
      return { amount: t('listing.price.contact', 'Prix sur demande'), period: '' };
    }

    return { amount: `${Math.round(best.v)} ${symbol}`, period: best.period };
  })();

  const formatTimeAgo = (date) => {
    try {
      return formatDistanceToNow(new Date(date), { 
        addSuffix: true, 
        locale: currentLocale 
      });
    } catch {
      return '';
    }
  };

  // Styles conditionnels - Hauteur optimisée pour compact
  const cardStyles = {
    height: layout === 'compact' ? (isProduct ? 'auto' : { xs: 200, sm: 160 }) : layout === 'comfortable' ? 200 : 280,
    minHeight: layout === 'compact' && isProduct ? 176 : undefined,
    overflow: 'visible',
    mb: layout === 'compact' ? 1.5 : 2, // Marge plus importante pour éviter le débordement
    mx: 0.5, // Marge horizontale pour éviter de toucher les bords
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e9e9e9',
    boxShadow: isSelected
      ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.18)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`
      : isHovered
        ? '0 10px 22px rgba(0,0,0,0.1)'
        : '0 2px 10px rgba(0,0,0,0.06)',
    transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
    borderRadius: 4,
    background: '#fff',
    '&:hover': {
      boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
      transform: 'translateY(-2px)',
      border: '1px solid #d8d8d8'
    }
  };

  if (layout === 'compact') {
    return (
      <Card
        sx={cardStyles}
        onClick={onSelect}
        onMouseEnter={() => {
          setIsHovered(true);
          onHover?.();
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box sx={{ display: 'flex', height: '100%', position: 'relative' }}>
          {/* Badge Stock Épuisé - Haut Gauche (Compact) */}
          {isProduct && itemData.isOutOfStock && (
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                left: 6,
                zIndex: 2,
                backgroundColor: '#ff385c',
                color: 'white',
                px: 0.75,
                py: 0.375,
                borderRadius: 0.75,
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: 0.3
              }}
            >
              {t('product.outOfStock', 'Épuisé')}
            </Box>
          )}

          {/* Section 1: Carrousel d'images (30%) */}
          <CarouselSection 
            images={images}
            title={itemData.title}
            layout="compact"
            compactFlex={isProduct ? '24%' : '30%'}
          />

          {/* Section 2: Informations (30%) */}
          <InfoSection 
            listing={itemData}
            layout="compact"
            formatLocation={formatLocation}
            formatTimeAgo={formatTimeAgo}
            t={t}
            isProduct={isProduct}
            inlinePrice={inlinePrice}
          />

          {/* Section 3: Prix (25%) */}
          <Box sx={{ flex: isProduct ? '0 0 20%' : '0 0 25%', display: 'flex', flexDirection: 'column', p: 1, justifyContent: 'center' }}>
            <PriceSection 
              listing={itemData}
              layout="compact"
              showIcon={true}
              isProduct={isProduct}
            />
          </Box>
          
          {/* Section 4: Avatar et actions (15%) */}
          <Box sx={{ flex: isProduct ? '0 0 22%' : '0 0 15%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0.5, gap: 0.5, justifyContent: 'space-between' }}>
            <AvatarActionsSection 
              listing={itemData}
              layout="compact"
              isFavorite={isFavorite}
              onFavoriteToggle={handleFavoriteToggle}
              t={t}
            />
            
            <IconButton
              size="small"
              color={isProduct ? 'success' : 'primary'}
              onClick={handleViewListing}
              aria-label={isProduct ? t('search.product.order', 'Commander') : t('search.view', 'Voir')}
              sx={{ width: 36, height: 36, p: 0.5, borderRadius: '50%' }}
            >
              <VisibilityIcon/>
            </IconButton>
          </Box>
        </Box>
      </Card>
    );
  }

  if (layout === 'airbnb') {
    const ownerData = itemData.owner || itemData.seller || null;
    console.log('[SearchResultCard airbnb] listing.booking_mode =', listing.booking_mode);
    return (
      <Card
        sx={{
          borderRadius: 3,
          border: isSelected ? `4px solid ${theme.palette.primary.main}` : '1px solid #ececec',
          boxShadow: isSelected ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.18)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}` : (isHovered ? '0 10px 24px rgba(0,0,0,0.12)' : '0 2px 10px rgba(0,0,0,0.06)'),
          transition: 'all 0.2s ease',
          background: '#fff',
          overflow: 'hidden',
          cursor: 'pointer',
          maxWidth: '100%',
          '&:hover': {
            boxShadow: '0 12px 26px rgba(0,0,0,0.14)',
            transform: 'translateY(-2px)'
          }
        }}
        onClick={onSelect}
        onMouseEnter={() => {
          setIsHovered(true);
          onHover?.();
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box sx={{ position: 'relative', aspectRatio: cardSize === 'tall' ? { xs: '0.72 / 1', md: '0.76 / 1' } : { xs: '0.88 / 1', md: '1 / 1' }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ height: cardSize === 'tall' ? { xs: '48%', md: '50%' } : { xs: '58%', md: '65%' }, overflow: 'hidden' }}>
            <CarouselSection
              images={images}
              title={itemData.title}
              layout="comfortable"
            />
          </Box>

          {/* Badge Stock Épuisé - Haut Gauche */}
          {isProduct && itemData.isOutOfStock && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 2,
                backgroundColor: '#ff385c',
                color: 'white',
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: 0.5
              }}
            >
              {t('product.outOfStock', 'Stock épuisé')}
            </Box>
          )}

          <IconButton
            onClick={handleFavoriteToggle}
            disabled={favoritePending}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 3,
              backgroundColor: 'rgba(255,255,255,0.92)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
            }}
          >
            {isFavorite ? <FavoriteIcon sx={{ color: '#ff385c' }} /> : <FavoriteBorderIcon />}
          </IconButton>

          <Box sx={{ height: cardSize === 'tall' ? { xs: '52%', md: '50%' } : { xs: '42%', md: '35%' }, p: { xs: 1.5, md: 1.25 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden' }}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                  {itemData.title}
                </Typography>
                {inlinePrice && (
                  <Typography variant="subtitle2" fontWeight={800} color="primary.main" noWrap sx={{ lineHeight: 1.2, flexShrink: 0 }}>
                    {inlinePrice.amount}
                  </Typography>
                )}
              </Box>
              {itemData.booking_mode ? (
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.25, width: '100%', display: 'block', wordWrap: 'break-word' }}>
                  {itemData.booking_mode === 'time_slot' || itemData.booking_mode === 'time_slot_capacity'
                    ? t('listing.bookingMode.slot', 'Par créneau')
                    : itemData.booking_mode === 'date_range'
                    ? t('listing.bookingMode.dateRange', 'Par nuitée')
                    : `Mode: ${itemData.booking_mode}`}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.disabled', mb: 0.25, width: '100%', display: 'block' }}>
                  (🕵️)
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatLocation(itemData)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <Rating value={parseFloat(itemData.rating_avg || 0) || 0} size="small" readOnly />
                <Typography variant="caption" color="text.secondary">
                  ({parseInt(itemData.review_count || 0, 10) || 0})
                </Typography>
              </Box>
              {cardSize === 'tall' && itemData.createdAt && (
                <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, fontSize: '0.7rem' }}>
                  {formatDistanceToNow(new Date(itemData.createdAt), { addSuffix: true, locale: currentLocale })}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box
                onClick={handleOwnerClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  minWidth: 0,
                  cursor: ownerData?.id ? 'pointer' : 'default',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                  transition: 'background-color 0.2s',
                  '&:hover': ownerData?.id ? { backgroundColor: theme.palette.action.hover } : {}
                }}
              >
                <Avatar
                  src={ownerData?.profilePicture || ownerData?.avatar}
                  sx={{ width: { xs: 30, md: 26 }, height: { xs: 30, md: 26 } }}
                >
                  {ownerData?.name?.[0] || 'V'}
                </Avatar>
                <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
                  {ownerData?.name || t('listing.owner.anonymous')}
                </Typography>
                {ownerData?.id && (
                  <OpenInNewIcon sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0, ml: 0.25 }} />
                )}
              </Box>
              <IconButton
                size="small"
                color={isProduct ? 'success' : 'primary'}
                onClick={handleViewListing}
                aria-label={isProduct ? t('search.product.order', 'Commander') : t('search.view', 'Voir')}
                sx={{ width: { xs: 40, md: 36 }, height: { xs: 40, md: 36 }, p: 0.5, borderRadius: '50%' }}
              >
                <VisibilityIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={cardStyles}
      onClick={onSelect}
      onMouseEnter={() => {
        setIsHovered(true);
        onHover?.();
      }}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Section 1: Carrousel avec overlays */}
      <CarouselSection 
        images={images}
        title={listing.title}
        layout={layout}
        isHovered={isHovered}
        badges={
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              maxWidth: images.length > 1 ? '60%' : '80%'
            }}
          >
            {isProduct && itemData.isOutOfStock && (
              <Chip
                label={t('product.outOfStock', 'Stock épuisé')}
                size="small"
                sx={{ 
                  backgroundColor: '#ff385c',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.7rem'
                }}
              />
            )}

            {listing.isVerified && (
              <Chip
                icon={<VerifiedIcon />}
                label={t('listing.verified')}
                size="small"
                color="success"
                sx={{ backgroundColor: 'rgba(76, 175, 80, 0.9)' }}
              />
            )}
            
            {listing.isAvailable && (
              <Chip
                label={t('listing.available')}
                size="small"
                color="primary"
                sx={{ backgroundColor: 'rgba(25, 118, 210, 0.9)' }}
              />
            )}
          </Box>
        }
        actions={
          <Fade in={isHovered}>
            <Box
              sx={{
                position: 'absolute',
                top: images.length > 1 ? 44 : 8,
                right: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              <Zoom in={isHovered} timeout={200}>
                <IconButton
                  size="medium"
                  onClick={handleFavoriteToggle}
                  disabled={favoritePending}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { 
                      backgroundColor: 'rgba(244, 67, 54, 0.15)',
                      transform: 'scale(1.15)',
                      boxShadow: '0 8px 24px rgba(244, 67, 54, 0.3)',
                      '& .favorite-icon': {
                        color: 'error.main',
                        transform: 'scale(1.1) rotate(10deg)',
                      }
                    }
                  }}
                >
                  {isFavorite ? 
                    <FavoriteIcon 
                      className="favorite-icon"
                      sx={{ 
                        color: 'error.main', 
                        fontSize: 32,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }} 
                    /> : 
                    <FavoriteBorderIcon 
                      className="favorite-icon"
                      sx={{ 
                        fontSize: 32,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }} 
                    />
                  }
                </IconButton>
              </Zoom>
              
              <Zoom in={isHovered} timeout={300}>
                <IconButton
                  size="small"
                  onClick={handleShare}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' }
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Zoom>
            </Box>
          </Fade>
        }
        priceOverlay={
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              color: 'text.primary',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: theme.shadows[3]
            }}
          >
            <PriceDisplay 
              listing={listing} 
              variant="compact" 
              showIcon={true}
            />
          </Box>
        }
      />

      {/* Contenu principal */}
      <CardContent sx={{ pb: 1 }}>
        {/* Section 2: Informations (titre + prix inline) */}
        <InfoSection 
          listing={itemData}
          layout={layout}
          formatLocation={formatLocation}
          formatTimeAgo={formatTimeAgo}
          t={t}
          isProduct={isProduct}
          inlinePrice={inlinePrice}
        />

        {/* Section 3: Prix détaillés */}
        <PriceSection 
          listing={itemData}
          layout={layout}
          showIcon={true}
          isProduct={isProduct}
        />
      </CardContent>

      {/* Section 4: Actions avec avatar */}
      <CardActions sx={{ pt: 0, justifyContent: 'space-between', px: 2, pb: 1.5 }}>
        <AvatarActionsSection 
          listing={listing}
          layout={layout}
          isFavorite={isFavorite}
          onFavoriteToggle={handleFavoriteToggle}
          t={t}
        />

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={isProduct ? t('search.product.orderTooltip', 'Commander ce produit') : "Voir l'annonce"}>
            <Button
              variant="contained"
              size="small"
              color={isProduct ? 'success' : 'primary'}
              onClick={handleViewListing}
              aria-label={isProduct ? t('search.product.order', 'Commander') : t('search.view', 'Voir')}
              sx={{ mr: 1, minWidth: 40, height: 36, px: 0.75, borderRadius: '8px' }}
            >
                <VisibilityIcon sx={{ fontSize: 24 }} />
            </Button>
          </Tooltip>
          
          <Tooltip title={t('listing.actions.message')}>
            <IconButton size="small" onClick={handleContact}>
              <MessageIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={t('listing.actions.call')}>
            <IconButton size="small" onClick={handleCall}>
              <PhoneIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

export default SearchResultCard;